<template>
  <MasterDetail
    data-test-id="walletPage"
    :detail-open="!!selectedAccount"
    @table:resize="tableWidth = $event"
  >
    <template #toolbar>
      <div class="wallet-toolbar elevation-4">
        <div class="wallet-search-bar">
          <v-text-field
            v-model="searchId"
            dense
            outlined
            hide-details
            clearable
            label="Search by Account ID (UUID)"
            class="wallet-search-input mr-2"
            data-test-id="walletSearchId"
            :disabled="runningAction"
            @keyup.enter="searchByAccountId"
          />
          <v-btn
            color="primary"
            :disabled="runningAction || !searchId"
            data-test-id="walletSearchIdBtn"
            @click="searchByAccountId"
          >
            <v-icon left>mdi-magnify</v-icon>
            Search ID
          </v-btn>
          <v-divider class="mx-4" vertical />
          <v-text-field
            v-model="searchReference"
            dense
            outlined
            hide-details
            clearable
            label="Search by Reference"
            class="wallet-search-input mr-2"
            data-test-id="walletSearchReference"
            :disabled="runningAction"
            @keyup.enter="searchByReference"
          />
          <v-btn
            color="primary"
            :disabled="runningAction || !searchReference"
            data-test-id="walletSearchReferenceBtn"
            @click="searchByReference"
          >
            <v-icon left>mdi-magnify</v-icon>
            Search Reference
          </v-btn>
        </div>
      </div>
    </template>

    <template #table>
      <!-- EMPTY STATE -->
      <div v-if="!hasSearched" class="wallet-empty-state">
        <v-icon large color="grey lighten-1">mdi-wallet-outline</v-icon>
        <div class="wallet-empty-text grey--text">
          Enter an account ID or reference to search for wallet accounts
        </div>
      </div>

      <!-- WALLET ACCOUNT TABLE -->
      <v-data-table
        v-else
        dense
        disable-pagination
        hide-default-footer
        fixed-header
        single-select
        class="wallet-table"
        :items="accounts"
        :headers="headers"
        :loading="runningAction"
        :height="tableHeight"
        no-data-text="No wallet accounts found"
      >
        <template v-slot:item="{ item }">
          <tr
            :class="{
              selected: selectedAccount && item.id === selectedAccount.id,
            }"
            :data-test-id="'wallet_account_' + item.id"
            @click="!runningAction && openAccount(item)"
          >
            <td>{{ item.reference }}</td>
            <td>{{ item.currency }}</td>
            <td>{{ item.balance }}</td>
            <td>{{ item.creditLimit }}</td>
            <td>
              <v-chip
                small
                :color="statusColor(item.status)"
                dark
              >
                {{ item.status }}
              </v-chip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </template>

    <template #detail>
      <!-- WALLET DETAIL -->
      <WalletDetail
        v-if="selectedAccount"
        data-test-id="walletDetail"
        :account-id="selectedAccount.id"
        :key="selectedAccount.id"
        :style="{ height: detailHeight + 'px' }"
        @close="closeDetail"
      />
    </template>
  </MasterDetail>
</template>

<script>
import MasterDetail from "../common/templates/MasterDetail.vue";
import WalletDetail from "./WalletDetail.vue";
import mainOverviewMixin from "../../mixins/main-overview-mixin";

export default {
  name: "WalletOverview",

  mixins: [mainOverviewMixin],

  components: {
    MasterDetail,
    WalletDetail,
  },

  data() {
    return {
      accounts: [],
      selectedAccount: null,
      runningAction: false,
      tableWidth: null,
      searchId: "",
      searchReference: "",
      hasSearched: false,
    };
  },

  async created() {
    // If route has an account param, load that account directly
    const accountParam = this.$route.params.account;
    if (accountParam) {
      await this.loadAccountById(accountParam);
    }
  },

  watch: {
    "$route.params.account": async function (accountId) {
      if (accountId) {
        if (!this.selectedAccount || this.selectedAccount.id !== accountId) {
          await this.loadAccountById(accountId);
        }
      } else {
        this.selectedAccount = null;
      }
    },
  },

  methods: {
    async searchByAccountId() {
      if (!this.searchId) return;
      this.runningAction = true;
      this.hasSearched = true;
      try {
        const account = await this.$store.$coreApi.coreWalletApi.getAccount(
          this.selectedDomain,
          this.searchId.trim()
        );
        if (account) {
          this.accounts = [account];
        } else {
          this.accounts = [];
        }
      } finally {
        this.runningAction = false;
      }
    },

    async searchByReference() {
      if (!this.searchReference) return;
      this.runningAction = true;
      this.hasSearched = true;
      try {
        const account = await this.$store.$coreApi.coreWalletApi.getAccountByReference(
          this.selectedDomain,
          this.searchReference.trim()
        );
        if (account) {
          this.accounts = [account];
        } else {
          this.accounts = [];
        }
      } finally {
        this.runningAction = false;
      }
    },

    async loadAccountById(accountId) {
      this.runningAction = true;
      this.hasSearched = true;
      try {
        const account = await this.$store.$coreApi.coreWalletApi.getAccount(
          this.selectedDomain,
          accountId
        );
        if (account) {
          this.accounts = [account];
          this.selectedAccount = account;
        }
      } finally {
        this.runningAction = false;
      }
    },

    async openAccount(account) {
      if (!this.selectedAccount || this.selectedAccount.id !== account.id) {
        await this.$router.push({
          name: "walletDetail",
          params: { account: account.id },
        });
        this.selectedAccount = account;
      }
    },

    async closeDetail() {
      await this.$router.push({ name: "wallet" });
      this.selectedAccount = null;
    },

    statusColor(status) {
      switch (status) {
        case "ACTIVE":
          return "green";
        case "SUSPENDED":
          return "orange";
        case "CLOSED":
          return "grey";
        default:
          return "grey";
      }
    },
  },

  computed: {
    selectedDomain() {
      return this.$store.state.selectedDomain;
    },

    headers() {
      return [
        { text: "Reference", value: "reference" },
        { text: "Currency", value: "currency" },
        { text: "Balance", value: "balance" },
        { text: "Credit Limit", value: "creditLimit" },
        { text: "Status", value: "status" },
      ];
    },

    showMinified() {
      return (
        (this.selectedAccount && this.tableWidth <= 900) ||
        this.getPageWidth() <= 900
      );
    },
  },
};
</script>

<style scoped>
* {
  text-align: left;
}

.wallet-toolbar {
  display: flex;
  flex-direction: column;
  flex: 0 1 100%;
  padding: 12px;
}

.wallet-search-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 80px;
}

.wallet-search-input {
  max-width: 300px;
}

.wallet-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 16px;
}

.wallet-empty-text {
  font-size: 1rem;
}

.wallet-table {
  max-height: 100%;
  padding: 0;
}

.wallet-table.v-data-table {
  display: flex;
  flex: 1 1 auto;
}

.wallet-table.v-data-table::v-deep > .v-data-table__wrapper {
  width: 100%;
}

.wallet-table.v-data-table::v-deep
  > .v-data-table__wrapper
  > table
  > tbody
  > tr {
  cursor: pointer;
}
</style>
